import { createSelector } from "reselect";
import { isDefined, uniqueByKey, memoize } from "@ls/js-utils";
import SelectionState from "state/selections/SelectionState";
import scope from "./scope";
import SummaryType from "state/selections/Constants/SummaryType";
import { selectSideBySideValue } from "state/selections/selector";
import { splitByCalcType } from "state/util";

export const selectFactorOptions = (state) => state[scope].factorOptions;
export const selectData = (summaryType) => (state) =>
  state[scope]["data"][summaryType];
export const selectFetchingData = (summaryType) => (state) =>
  state[scope]["fetchingData"][summaryType];
export const selectTeSbsData = (summaryType) => (state) =>
  state[scope]["teSbsData"][summaryType];
export const selectFetchingTeSbsData = (summaryType) => (state) =>
  state[scope]["fetchingTeSbsData"][summaryType];

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return (state) => state[scope]["definitions"][selectionState];
  }

  return createSelector(
    (state) => state[scope]["definitions"][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(
        ({ complete }) => complete
      );
      return completeDefinitions.filter(
        uniqueByKey(completeDefinitions, "key")
      );
    }
  );
});

export const selectRowsMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectData(SummaryType.ABSOLUTE),
  selectData(SummaryType.RELATIVE),
  selectTeSbsData(SummaryType.ABSOLUTE),
  selectTeSbsData(SummaryType.RELATIVE),
  selectSideBySideValue(SelectionState.COMMITTED),
  (definitions, absData, relData, absSbsData, relSbsData, sideBySide) => {
    const absTeData = absData?.trackingErrorSummaries ?? {};
    const relsTeData = relData?.trackingErrorSummaries ?? {};
    const absSbsTeData = absSbsData?.trackingErrorSummaries ?? {};
    const relSbsTeData = relSbsData?.trackingErrorSummaries ?? {};
    const [absDefinitions, relDefinitions] = splitByCalcType(definitions);
    const entityRowsMap = {};
    const toRowsMap = (teDefinitions, trackingErrorSummaries, scenario) => {
      if (trackingErrorSummaries?.length) {
        trackingErrorSummaries.forEach((teSummary) => {
          const { primaryKey = {}, values = {} } = teSummary;
          const { entityId, covarType } = primaryKey;

          if (!isDefined(entityRowsMap[entityId])) {
            entityRowsMap[entityId] = {};
          }

          teDefinitions
            .filter(({ covarianceType }) => covarianceType === covarType)
            .forEach((teDefinition) => {
              const { factor, covarianceType, summary } = teDefinition;
              const value = values[factor.toLowerCase()]; //factors are keyed by lower case values for some reason
              const colId = `te_${summary}_${covarianceType}_${factor}${
                scenario ? ` - scenario` : ""
              }`;
              if (isDefined(value)) {
                entityRowsMap[entityId][colId] = value;
              }
            });
        });
      }
    };
    toRowsMap(absDefinitions, absTeData);
    toRowsMap(relDefinitions, relsTeData);
    if (sideBySide) {
      toRowsMap(absDefinitions, absSbsTeData, true);
      toRowsMap(relDefinitions, relSbsTeData, true);
    }
    return entityRowsMap;
  },
);
